/**
 * The loadout strip (2026-09-02).
 *
 * User: "add below (where talent button is) inventory, what I equip on match
 * and what I will carry on match."
 *
 * ## It shows; it does not assign
 *
 * `backend/src/game/consumables/sim/satchel.ts` argued against building a
 * loadout *screen* — with one item per slot per class the assignment is
 * forced, and "a screen whose every outcome is forced is friction, not a
 * choice". Nothing here is draggable or assignable, so that still holds. The
 * question this answers is the one the hub could not answer at all: what am I
 * about to walk in with? Your weapon, your armour and your satchel were only
 * ever visible one tab deep in the Shop, or not at all.
 *
 * ## One row, not two panels
 *
 * It sits inside the hero card, above a live character render that is the
 * best thing on this screen. Every pixel it takes comes off the delver, so it
 * is a single row of tiles with a divider — worn on the left, carried on the
 * right — rather than the two stacked sections the ask would naturally
 * produce. The numbers behind each tile (damage, swing, reach, reduction)
 * live in its `title`, so the strip stays scannable and still answers a
 * question when asked.
 *
 * Pure string builders, same as the rest of `ui/`.
 */

import type { LoadoutRow } from '../net/loadout-api';
import type { CharacterRosterEntry } from '../config/character-roster';
import { gearIconSrc, consumableIconSrc, esc, ICON_FALLBACK_ATTR } from './item-icon';
import { armourClassLabel } from './paperdoll';
import { N_, t, tp, formatNumber } from '../i18n/i18n';

/**
 * Display names for the roster's equipment filenames.
 *
 * Keyed by the model name rather than by class, so a class that changes
 * weapon changes its label for free — and an unmapped model falls back to a
 * title-cased version of its own filename rather than rendering blank.
 */
const GEAR_NAME: Record<string, string> = {
  dagger: N_('Dagger'),
  sword_1handed: N_('Arming Sword'),
  axe_2handed: N_('Great Axe'),
  druid_staff: N_('Thornstaff'),
  shield_round: N_('Round Shield'),
};

export function gearName(model: string): string {
  const name = GEAR_NAME[model];
  return name ? t(name) : model.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** The weapon class word — `MEDIUM` -> "Medium" — in the player's language. */
function weaponClassLabel(weaponClass: string): string {
  switch (weaponClass) {
    case 'LIGHT': return t('Light');
    case 'MEDIUM': return t('Medium');
    case 'BLUNT': return t('Blunt');
    case 'HEAVY': return t('Heavy');
    case 'REACH': return t('Reach');
    default: return `${weaponClass.charAt(0)}${weaponClass.slice(1).toLowerCase()}`;
  }
}

/** "Light · 18 dmg · 0.42s swing · 1.6m reach" — the whole feel of the weapon in one string. */
export function weaponTitle(name: string, gear: LoadoutRow['gear']): string {
  return t('{name} — {weight} · {damage} damage · {swing}s swing · {reach}m reach · {kg}kg', {
    name,
    weight: weaponClassLabel(gear.weaponClass),
    damage: gear.damage,
    swing: formatNumber(gear.swingMs / 1000, 2),
    reach: formatNumber(gear.reachM),
    kg: formatNumber(gear.weightKg),
  });
}

/**
 * Armour as a percentage pair, because that is the trade it actually is.
 *
 * PLATE turns aside 30% of physical and 0% of magic; CLOTH is the exact
 * inverse. A player choosing between a knight and a druid is choosing which
 * half of the damage in the dungeon they are soft to, and no screen said so.
 */
export function armorTitle(gear: LoadoutRow['gear']): string {
  const pct = (v: number): string => `${Math.round(v * 100)}%`;
  const speed = Math.round((1 - gear.moveSpeedMultiplier) * 100);
  const vars = {
    armour: armourClassLabel(gear.armorClass),
    physical: pct(gear.physicalReduction),
    magic: pct(gear.magicResistance),
    speed,
  };
  const base = speed > 0
    ? t('{armour} — {physical} physical · {magic} magic · {speed}% slower', vars)
    : t('{armour} — {physical} physical · {magic} magic · no speed cost', vars);
  // Passive dodge (2026-10-16), unladen — the run halves it under a full pack.
  if (!gear.dodgeChance) return base;
  return t('{armour} · {dodge} dodge', {
    armour: base, dodge: `${formatNumber(Math.round(gear.dodgeChance * 1000) / 10)}%`,
  });
}

/** 0–100. Clamped, and empty rather than NaN for a missing or zero capacity. */
export function weightPercent(startWeightKg: number, capacityKg: number): number {
  if (!(capacityKg > 0)) return 0;
  if (!Number.isFinite(startWeightKg) || startWeightKg <= 0) return 0;
  return Math.min(100, (startWeightKg / capacityKg) * 100);
}

/**
 * The weight line.
 *
 * Phrased as headroom rather than as a load: at the door you are carrying a
 * weapon and a few flasks against a 25kg cap, so a bar reading 15% is not
 * "you are heavy", it is "this is what you have left for loot" — which is the
 * only reason an extraction player looks at it.
 */
export function weightLineMarkup(startWeightKg: number, capacityKg: number): string {
  const pct = weightPercent(startWeightKg, capacityKg);
  const free = Math.max(0, Math.round((capacityKg - startWeightKg) * 10) / 10);
  return `
    <div class="flex items-center gap-2 mt-2">
      <div class="meter flex-1 h-2">
        <div class="meter-fill absolute inset-y-0 left-0 bg-warn" style="width:${pct}%"></div>
      </div>
      <span class="text-[11px] text-text-dim tabular-nums shrink-0">${esc(t('{free}kg free of {cap}', { free: formatNumber(free), cap: formatNumber(capacityKg) }))}</span>
    </div>
  `;
}

/**
 * A 44px slot — CLAUDE.md's tap floor, and small enough that a row of six
 * still fits a 320px screen.
 *
 * Not `itemTileMarkup`: that tile is tuned for the shop's dark cards, where
 * `bg-black/20` behind a prop is fine because the card around it is dark too.
 * Here the props are grey steel on a wooden plaque, and on that ground a
 * sword read as a sliver of nothing. These get a lit plate instead, the same
 * one the hub's own panels are cut from.
 */
const SLOT_PLATE = 'rounded-md border border-[#57462f] '
  + 'bg-[radial-gradient(circle_at_50%_26%,#5b4a37,#2b2219_74%)] shadow-[inset_0_1px_0_rgba(255,222,176,0.10)]';

function slotMarkup(src: string, alt: string, title: string, badge?: string): string {
  const chip = badge
    ? `<span class="absolute -bottom-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full
                    border border-[#2b2219] bg-[#d8ab5c] text-[#241a10] text-[10px] font-bold tabular-nums">${esc(badge)}</span>`
    : '';
  return `
    <span class="relative block w-11 h-11 shrink-0 ${SLOT_PLATE}" title="${esc(title)}" aria-label="${esc(title)}">
      <img ${ICON_FALLBACK_ATTR} src="${esc(src)}" alt="${esc(alt)}" loading="lazy"
           class="absolute inset-0 w-full h-full object-contain p-1" />
      ${chip}
    </span>
  `;
}

/** Reads as an empty pocket rather than as a tile that failed to load. */
function emptySlotMarkup(hint: string): string {
  return `
    <span class="frame-empty block w-11 h-11 shrink-0
                 grid place-items-center text-[10px] leading-tight text-text-dim text-center px-0.5"
          title="${esc(hint)}">${esc(hint)}</span>
  `;
}

export interface LoadoutMarkupOptions {
  loadout: LoadoutRow | null;
  roster: CharacterRosterEntry;
  /** Shown while the first fetch is in flight, so the card does not jump on arrival. */
  loading?: boolean;
}

export function loadoutMarkup({ loadout, roster, loading }: LoadoutMarkupOptions): string {
  if (!loadout) {
    return `<p class="text-[11px] text-text-dim">${esc(loading ? t('Reading your kit…') : t('Kit unavailable.'))}</p>`;
  }
  // design/gdd/equipment.md — the equipped arms, not the class's defaults.
  // Falling back to the roster keeps the strip drawable for a delver whose
  // loadout request has not landed yet.
  const weaponModel = loadout.gear.weaponModel || roster.weaponModel;
  const weapon = loadout.gear.weaponName ? t(loadout.gear.weaponName) : gearName(weaponModel);
  const worn = [
    slotMarkup(gearIconSrc(weaponModel), weapon, weaponTitle(weapon, loadout.gear)),
  ];
  // Armour and dodge are a visible line under the strip (QA 2026-10-16), not
  // only the off-hand's tooltip: a barbarian has no off-hand, and a phone has
  // no hover, so most delvers never saw either number.
  if (loadout.gear.offhandModel) {
    const shield = loadout.gear.offhandName ? t(loadout.gear.offhandName) : gearName(loadout.gear.offhandModel);
    worn.push(slotMarkup(gearIconSrc(loadout.gear.offhandModel), shield, t('{name} — {detail}', { name: shield, detail: armorTitle(loadout.gear) })));
  }

  const carried = loadout.satchel.length > 0
    ? loadout.satchel.map((s) => slotMarkup(
        consumableIconSrc(s.defId),
        t(s.name),
        tp(s.charges, '{name} — {n} charge · slot {slot} · {kg}kg', '{name} — {n} charges · slot {slot} · {kg}kg', {
          name: t(s.name), slot: s.slot, kg: s.weightKg,
        }),
        String(s.charges),
      ))
    : [emptySlotMarkup(t('Empty'))];

  return `
    <div class="flex items-stretch gap-3 text-left">
      <div class="min-w-0">
        <p class="font-heading font-bold text-[13px] text-[#c2a06a] mb-1">${esc(t('Wearing'))}</p>
        <div class="flex gap-1.5">${worn.join('')}</div>
      </div>
      <span class="w-px bg-[#4a3c2d]/70 self-stretch shrink-0" aria-hidden="true"></span>
      <div class="min-w-0 flex-1">
        <p class="font-heading font-bold text-[13px] text-[#c2a06a] mb-1">${esc(t('Carrying'))}</p>
        <div class="flex gap-1.5 flex-wrap">${carried.join('')}</div>
      </div>
    </div>
    <p class="mt-1.5 text-[11px] leading-snug text-text-dim" data-loadout="armour">${esc(armorTitle(loadout.gear))}</p>
    ${weightLineMarkup(loadout.startWeightKg, loadout.capacityKg)}
  `;
}
